/**
 * Art History Learning: one artwork card per (user + artwork), each card holds multiple flows.
 * Same artwork can have many flows (e.g. compared with different artworks).
 * - Artwork card doc: artHistoryFlows/{userId}_{artworkId}
 * - Flow doc: artHistoryFlows/{userId}_{artworkId}/flows/{flowId}
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';

const COLLECTION = 'artHistoryFlows';
const FLOWS_SUB = 'flows';

/** Stable artwork id: prefer artwork.id (from Firestore), fallback to imageUrl for backwards compat. */
export function getPrimaryArtworkKey(primary) {
  if (!primary) return '';
  const id = (primary.id || '').toString().trim();
  const url = (primary.imageUrl || primary.originalImageUrl || primary.coverUrl || '').toString().trim();
  const raw = id || url;
  if (!raw) return '';
  const normalized = raw.replace(/\s+/g, '-');
  return normalized.slice(0, 200);
}

/** Artwork card doc ID: one card per user per artwork. */
function artworkCardId(uid, primaryArtwork) {
  const key = getPrimaryArtworkKey(primaryArtwork);
  const safe = key.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 150);
  return `${uid}_${safe}`;
}

/** Generate a unique flow id. */
function generateFlowId() {
  return `flow_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Minimal primary artwork snapshot for display. */
function primarySnapshot(artwork) {
  if (!artwork) return null;
  return {
    id: artwork.id,
    title: artwork.title || 'Untitled',
    imageUrl: artwork.imageUrl || artwork.originalImageUrl || artwork.coverUrl || '',
    artistName: artwork.artistName || artwork.artist || 'Unknown',
    artMovement: artwork.artMovement || artwork.movement || '',
  };
}

/** Minimal comparison artwork snapshot. */
function comparisonSnapshot(artwork) {
  if (!artwork) return null;
  return {
    id: artwork.id,
    title: artwork.title || 'Untitled',
    imageUrl: artwork.imageUrl || artwork.originalImageUrl || '',
    artistName: artwork.artistName || artwork.artist || 'Unknown',
    artMovement: artwork.artMovement || artwork.movement || '',
  };
}

/**
 * Get all artwork cards for the user (each card = one artwork, holds multiple flows).
 * @returns {Promise<Array<{ id, primaryArtwork, lastFlowId, lastFlowUpdatedAt, ... }>>}
 */
export async function getArtworkCards(uid) {
  if (!uid) return [];
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', uid)
    );
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => {
      const ta = a.lastFlowUpdatedAt?.toMillis?.() ?? a.lastFlowUpdatedAt?._seconds * 1000 ?? 0;
      const tb = b.lastFlowUpdatedAt?.toMillis?.() ?? b.lastFlowUpdatedAt?._seconds * 1000 ?? 0;
      return tb - ta;
    });
    return list;
  } catch (e) {
    console.warn('[artHistoryFlowService] getArtworkCards failed:', e);
    return [];
  }
}

/**
 * Get a specific flow (by flowId) or the latest flow for this user + artwork.
 * @returns {Promise<{ id: flowId, step1Complete, step2Complete, step3Complete, step2Data, step3Data, ... } | null>}
 */
export async function getFlowForArtwork(uid, primaryArtwork, flowId = null) {
  if (!uid || !primaryArtwork) return null;
  const cardId = artworkCardId(uid, primaryArtwork);
  const flowsRef = collection(db, COLLECTION, cardId, FLOWS_SUB);
  try {
    if (flowId) {
      const flowRef = doc(flowsRef, flowId);
      const snap = await getDoc(flowRef);
      if (!snap.exists()) return null;
      const data = snap.data();
      return {
        id: snap.id,
        ...data,
        step1Complete: !!data.step1Complete,
        step2Complete: !!data.step2Complete,
        step3Complete: !!data.step3Complete,
        uploadedActivityAt: data.uploadedActivityAt ?? null,
      };
    }
    const q = query(flowsRef, orderBy('updatedAt', 'desc'), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const d = snapshot.docs[0];
    const data = d.data();
    return {
      id: d.id,
      ...data,
      step1Complete: !!data.step1Complete,
      step2Complete: !!data.step2Complete,
      step3Complete: !!data.step3Complete,
      uploadedActivityAt: data.uploadedActivityAt ?? null,
    };
  } catch (e) {
    console.warn('[artHistoryFlowService] getFlowForArtwork failed:', e);
    return null;
  }
}

/**
 * Ensure artwork card exists; create flow doc with step1Complete. Returns flowId.
 * @param cardMeta - Optional { artworkTypeKey, level, artworkTypeLabel } to persist on the card so compare step can load artworks on resume.
 */
export async function saveFlowStep1(uid, primaryArtwork, step1Data = {}, cardMeta = {}) {
  if (!uid || !primaryArtwork) return null;
  const key = getPrimaryArtworkKey(primaryArtwork);
  if (!key) return null;
  const cardId = artworkCardId(uid, primaryArtwork);
  const cardRef = doc(db, COLLECTION, cardId);
  const primary = primarySnapshot(primaryArtwork);
  const flowId = generateFlowId();
  const flowRef = doc(db, COLLECTION, cardId, FLOWS_SUB, flowId);

  const cardSnap = await getDoc(cardRef);
  const cardData = {
    userId: uid,
    primaryArtworkId: key,
    primaryArtwork: primary,
    lastFlowId: flowId,
    lastFlowUpdatedAt: serverTimestamp(),
    lastFlowSummary: { step1Complete: true, step2Complete: false, step3Complete: false },
    updatedAt: serverTimestamp(),
    ...(cardSnap.exists() ? {} : { createdAt: serverTimestamp() }),
    ...(cardMeta.artworkTypeKey != null && { artworkTypeKey: cardMeta.artworkTypeKey }),
    ...(cardMeta.level != null && { level: cardMeta.level }),
    ...(cardMeta.artworkTypeLabel != null && cardMeta.artworkTypeLabel !== '' && { artworkTypeLabel: cardMeta.artworkTypeLabel }),
  };
  const flowData = {
    step1Complete: true,
    step1Data: { ...step1Data },
    step2Complete: false,
    step3Complete: false,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  };

  // Sequential writes so the card exists before the flow write (rules use get() on parent)
  await setDoc(cardRef, cardData, { merge: true });
  await setDoc(flowRef, flowData);
  return flowId;
}

/**
 * Persist Step 1 analysis snapshot (appreciation + discovery) so resume loads instantly without re-fetching cache.
 * Call after analysis completes and whenever `currentFlowId` is known.
 */
export async function mergeFlowStep1Data(uid, primaryArtwork, flowId, step1Payload) {
  if (!uid || !primaryArtwork || !flowId || !step1Payload || typeof step1Payload !== 'object') return;
  const cardId = artworkCardId(uid, primaryArtwork);
  const flowRef = doc(db, COLLECTION, cardId, FLOWS_SUB, flowId);
  let safe = step1Payload;
  try {
    safe = JSON.parse(JSON.stringify(step1Payload));
  } catch (_) {
    /* keep reference */
  }
  await setDoc(
    flowRef,
    {
      step1Complete: true,
      step1Data: safe,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  const cardRef = doc(db, COLLECTION, cardId);
  await setDoc(
    cardRef,
    {
      lastFlowUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Update flow with Step 2 (comparison complete).
 */
export async function saveFlowStep2(uid, primaryArtwork, flowId, comparisonArtwork, comparisonResult) {
  if (!uid || !primaryArtwork || !flowId) return;
  const cardId = artworkCardId(uid, primaryArtwork);
  const flowRef = doc(db, COLLECTION, cardId, FLOWS_SUB, flowId);
  const comparison = comparisonSnapshot(comparisonArtwork);
  const cardRef = doc(db, COLLECTION, cardId);
  await setDoc(flowRef, {
    step1Complete: true,
    step2Complete: true,
    step2Data: {
      comparisonArtwork: comparison,
      comparisonResult: comparisonResult || null,
    },
    step3Complete: false,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await setDoc(cardRef, {
    lastFlowId: flowId,
    lastFlowUpdatedAt: serverTimestamp(),
    lastFlowSummary: { step1Complete: true, step2Complete: true, step3Complete: false },
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Update flow with Step 3 (activity generated).
 */
export async function saveFlowStep3(uid, primaryArtwork, flowId, comparisonArtwork, step3Activity) {
  if (!uid || !primaryArtwork || !flowId) return;
  const cardId = artworkCardId(uid, primaryArtwork);
  const flowRef = doc(db, COLLECTION, cardId, FLOWS_SUB, flowId);
  const comparison = comparisonSnapshot(comparisonArtwork);
  const activityData = step3Activity && typeof step3Activity === 'object'
    ? {
        title: step3Activity.title || '',
        objective: step3Activity.objective || step3Activity.whatYoullMake || '',
        materials: Array.isArray(step3Activity.materials) ? step3Activity.materials : [],
        steps: Array.isArray(step3Activity.steps) ? step3Activity.steps : [],
      }
    : null;
  const cardRef = doc(db, COLLECTION, cardId);
  await setDoc(flowRef, {
    step1Complete: true,
    step2Complete: true,
    step3Complete: true,
    step3Data: {
      comparisonArtwork: comparison,
      activity: activityData,
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await setDoc(cardRef, {
    lastFlowId: flowId,
    lastFlowUpdatedAt: serverTimestamp(),
    lastFlowSummary: { step1Complete: true, step2Complete: true, step3Complete: true },
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Mark that the user has uploaded their activity artwork for this flow.
 */
export async function markFlowUploaded(uid, primaryArtwork, flowId) {
  if (!uid || !primaryArtwork || !flowId) return;
  const cardId = artworkCardId(uid, primaryArtwork);
  const flowRef = doc(db, COLLECTION, cardId, FLOWS_SUB, flowId);
  const cardRef = doc(db, COLLECTION, cardId);
  await setDoc(flowRef, {
    uploadedActivityAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await setDoc(cardRef, {
    lastFlowUploadedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
